import { StakeholderGroup } from '../model/stakeholderGroup';
import { UtrvType } from '../constants/status';
import { AppConfig } from './app';
import { OrganizationUserRole } from '@constants/user'
import { OnboardingStep } from './common';
import Fuse from 'fuse.js';
import { InitiativeSuggestion } from '@g17eco/types/initiativeSuggestion';
import { DelegationScope } from '@g17eco/types/surveyDelegation';
import { SafeUser, UserMin, UserPermissions } from '@g17eco/types/user';
import { SurveyScope } from './surveyCommon';

interface BasicUserRolePermissions {
  permissions: UserPermissions[];
}

export enum OnboardingStatus {
  NotStarted = 'not_started',
  Pending = 'pending',
  Started = 'started',
  Rejected = 'rejected',
  Complete = 'complete',
  Deleted = 'deleted',
}

export enum ObType {
  JoinRequest = 'join_request',
  Initiative = 'initiative',
  Organization = 'organization',
}

interface SurveyConfig {
  code: string;
  surveyId?: string;
  sourceName: string;
  effectiveDate: Date;
  utrvType: UtrvType;
  evidenceRequired: boolean;
  verificationRequired: boolean;
  scope?: SurveyScope;
  complete: boolean;
}

interface OnboardingUser extends BasicUserRolePermissions {
  emailTemplate?: string;
  userId?: string;
  sfId?: string;
  firstName?: string;
  surname?: string;
  email: string;
  complete: boolean;
  message?: string;
}

interface SurveyRoles {
  admin: string[],
  viewer: string[]
}

export interface OrganizationOnboardingRole {
  permissions: OrganizationUserRole[];
  modelId: string;
}

export enum ObNotificationCode {
  FirstReminder = 'first_reminder',
  FinalReminder = 'final_reminder',
  ManualReminder = 'manual_reminder',
}

export const eventLabelMap = {
  [ObNotificationCode.FirstReminder]: 'First reminder',
  [ObNotificationCode.FinalReminder]: 'Final reminder',
  [ObNotificationCode.ManualReminder]: 'Manual reminder',
};

interface NotificationItem {
  type: 'email';
  code: ObNotificationCode;
  referenceId?: string;
  completedDate: string;
}

interface OnboardingCommon {
  user: OnboardingUser;
  initiativeId?: string;
  organizationId?: string;
  type: ObType;
  utrvConfig?: StakeholderGroup;
  surveyStakeholders?: StakeholderGroup;
  assuranceStakeholders?: StakeholderGroup;
  surveyRoles?: SurveyRoles;
  surveyIds?: string[];
  delegationScope?: DelegationScope;
  surveyConfig?: SurveyConfig;
  createdBy?: string;
  organizationRoles?: OrganizationOnboardingRole[];
  notifications?: {
    items: NotificationItem[];
  };
}

export interface OnboardingModelPlain extends OnboardingCommon {
  _id: string;
  token: string;
  status: OnboardingStatus;
  created: string;
  onboardingListId?: string;
  surveyConfig?: SurveyConfig;
}

export interface OnboardingToken extends OnboardingModelPlain {
  initiativeName: string;
  externalIdp?: boolean;
  permissionGroup?: string;
  appConfig?: AppConfig;
}

export type ReferralInfoType = {
  [referralInfoKey: string]: string | null,
}

export interface MonthDay {
  month: string;
  day: string;
}

export const getStepTitle = (step: OnboardingStep): string => {
  switch (step) {
    case OnboardingStep.CompanyInfo:
    case OnboardingStep.SGXReferrerCodeIssuerLookup:
      return 'Company details';
    case OnboardingStep.IssuerCompanyOnboardOrNot:
      return 'Onboarding';
    case OnboardingStep.EmailDomainCompanyOnboardOrNot:
      return 'Company check';
    case OnboardingStep.SectorInfo:
      return 'Sector info';
    case OnboardingStep.ReferralInfo:
      return 'Referral info';
    case OnboardingStep.Signup:
      return 'Sign up';
    case OnboardingStep.VerifyEmail:
      return 'Verify email';
    case OnboardingStep.Activation:
      return 'Activation';
    case OnboardingStep.CreateAssessment:
      return 'Assessment details';
    case OnboardingStep.Complete:
    case OnboardingStep.CompleteMAT:
      return 'Finished';
    default:
      return '';
  }
}

export interface ActivationToken {
  _id: string;
  created: string;
  expiryDate: string;
  token: string;
  userId: string;
}

export interface OnboardingListPlain {
  _id: string;
  name: string;
  code: string;
  fileName?: string;
  initiativeId?: string;
  createdBy?: string;
  created: string;
}

export interface OnboardingListExtended extends OnboardingListPlain {
  onboardings: OnboardingModelPlain[];
  creator: UserMin;
}

export interface OnboardingForm {
  email: string;
  firstName: string;
  surname: string;
  password: string;
  rPassword: string;
  userAgreement: boolean;
  country: string;
}

export enum OnboardingType {
  Default = 'default',
  CustomMetric = 'custom_metric',
}

export interface CSVFormat {
  companyName: string;
  email: string;
  firstName?: string;
  lastName?: string;
  weight?: number;
  surveyScope?: string[];
  initiativeId?: string;
}

export interface CreateOnboardingData {
  name: string | undefined;
  email: string | undefined;
  onboardingType: OnboardingType;
  effectiveDate?: Date;
  isVerified?: boolean;
  isPremium?: boolean;
  isPublic?: boolean;
  createSurvey?: boolean;
  customMetricIds?: string[];
}

export interface CreateCompanyOnboardingImport {
  portfolioId: string;
  companies: CSVFormat[];
}

export enum EmailDeliveryType {
  Pending = 'pending',
  Delivery = 'delivery',
  Bounce = 'bounce',
  Complain = 'complain',
}

export enum EmailDeliverySubType {
  Permanent = 'permanent', // hard-bounce
  Transient = 'transient', // soft-bounce
}

export interface OnboardingEmailTransaction {
  id?: string;
  externalId?: string;
  onboardingId: string;
  event: ObNotificationCode;
  type?: EmailDeliveryType;
  subType?: string;
  createdAt?: string;
  updatedAt?: string;
}

export type ManagementUser = Pick<
  SafeUser,
  '_id' | 'permissions' | 'firstName' | 'surname' | 'isStaff' | 'lastLogin' | 'email'
> & { invitedDate?: string };

export type ManagementInvitation = Pick<
  OnboardingModelPlain,
  '_id' | 'created' | 'user' | 'status' | 'type'
> & {
  initiativeId: string;
};

export type ManagementOrganizationInvitation = Pick<
  OnboardingModelPlain,
  '_id' | 'created' | 'user' | 'status' | 'type'
> & {
  organizationId: string;
  organizationRoles: OrganizationOnboardingRole[];
};

export interface CompanyImportRow {
  id: number;
  matches?: Fuse.FuseResult<InitiativeSuggestion>[];
  data: CSVFormat;
}
